import type { EventDetailsDto, EventParticipantCandidateDto, EventParticipantDto } from "@/api/types";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { Trash01Icon } from "@/components/Icons/Trash01Icon";
import { UserPlus01Icon } from "@/components/Icons/UserPlus01Icon";
import { List } from "@/components/List/List";
import { useModalState } from "@/components/Modal/Modal";
import { User } from "@/components/User/User";
import { Button } from "@/design-system/Button";
import { SectionCard } from "@/design-system/SectionCard";
import { Spread } from "@/design-system/Spread";
import { computeMaxGuestsForExistingParticipant, computeMaxGuestsForNewParticipant } from "@/helpers/events";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { eventMutations } from "@/queries/events/mutations";
import { useTranslation } from "@/translations";

import { AddEventParticipantModal } from "./AddEventParticipantModal";
import { EventParticipantGuestsModal } from "./EventParticipantGuestsModal";

type GuestModalData =
  | { mode: "add"; participant: EventParticipantCandidateDto; maxGuests: number }
  | { mode: "edit"; participant: EventParticipantDto; maxGuests: number };

export function EventParticipantsCard({ event }: { event: EventDetailsDto }): React.ReactNode {
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const [isAddModalOpen, addModal] = useBool(false);
  const guestModal = useModalState<GuestModalData>();
  const removeModal = useModalState<EventParticipantDto>();
  const registerParticipant = eventMutations.useRegisterEventParticipant();
  const updateParticipant = eventMutations.useUpdateEventParticipant();
  const removeParticipant = eventMutations.useRemoveEventParticipant();
  const amountParticipants = event.participantCount ?? 0;
  const isFull = event.maxParticipants !== undefined && amountParticipants >= event.maxParticipants;

  const handleCandidateSelect = (participant: EventParticipantCandidateDto) => {
    const maxGuests = computeMaxGuestsForNewParticipant(event);
    if (maxGuests > 0) {
      guestModal.open({ mode: "add", participant, maxGuests });

      return;
    }

    registerParticipant.mutate(
      { eventId: event.id, data: { userId: participant.id, guestCount: 0 } },
      { onSuccess: addModal.setFalse },
    );
  };

  const handleGuestCountConfirm = (guestCount: number) => {
    const data = guestModal.data;
    if (!data) {
      return;
    }

    if (data.mode === "add") {
      registerParticipant.mutate(
        { eventId: event.id, data: { userId: data.participant.id, guestCount } },
        {
          onSuccess: () => {
            guestModal.requestClose();
            addModal.setFalse();
          },
        },
      );

      return;
    }

    updateParticipant.mutate(
      { eventId: event.id, userId: data.participant.id, data: { guestCount } },
      { onSuccess: guestModal.requestClose },
    );
  };

  const handleRemove = () => {
    if (!removeModal.data) {
      return;
    }

    removeParticipant.mutate(
      { eventId: event.id, userId: removeModal.data.id },
      { onSuccess: removeModal.requestClose },
    );
  };

  return (
    <>
      <SectionCard
        title={t("page.event.details.participants.details", { count: amountParticipants })}
        description={t("page.event.details.participants.details-with-guests", {
          count: amountParticipants - event.guestCount,
          guests: event.guestCount,
        })}
        renderAction={
          event.canManageParticipants
            ? () => (
                <Button
                  variant="subtle"
                  size="sm"
                  icon={<UserPlus01Icon />}
                  onClick={addModal.setTrue}
                  disabled={isFull}
                  data-testid="add-event-participant"
                >
                  {t("common.action.add")}
                </Button>
              )
            : undefined
        }
      >
        {amountParticipants > 0 && (
          <List getKey={(participant) => participant.id} items={event.participants}>
            {(participant) => (
              <Spread space="sm" align="center">
                <User user={participant} renderSubtitle={(user) => user.locatedAt} />
                <Spread space="sm" align="center">
                  {participant.guestCount > 0 && (
                    <span className="text-caption text-text-secondary">
                      ({t("page.event.details.participants.guest-count", { count: participant.guestCount })})
                    </span>
                  )}
                  {event.canManageParticipants && participant.id !== sessionUser.id && (
                    <ContextMenu items={getParticipantActions(participant)} />
                  )}
                </Spread>
              </Spread>
            )}
          </List>
        )}
      </SectionCard>

      <AddEventParticipantModal
        eventId={event.id}
        isOpened={isAddModalOpen}
        onOpenChange={addModal.set}
        isAdding={registerParticipant.isPending}
        onSelect={handleCandidateSelect}
      />

      {guestModal.data && (
        <EventParticipantGuestsModal
          key={`${guestModal.data.mode}-${guestModal.data.participant.id}`}
          participantName={guestModal.data.participant.fullName}
          initialGuestCount={guestModal.data.mode === "edit" ? guestModal.data.participant.guestCount : 0}
          maxGuests={guestModal.data.maxGuests}
          submitLabel={guestModal.data.mode === "add" ? t("common.action.add") : t("common.action.save")}
          isLoading={registerParticipant.isPending || updateParticipant.isPending}
          isOpened={guestModal.isOpen}
          onOpenChange={(open) => {
            if (!open) {
              guestModal.requestClose();
            }
          }}
          onConfirm={handleGuestCountConfirm}
        />
      )}

      {removeModal.data && (
        <ConfirmModal
          title={t("page.event.participants.remove.title")}
          description={t("page.event.participants.remove.description", { name: removeModal.data.fullName })}
          isLoading={removeParticipant.isPending}
          isOpened={removeModal.isOpen}
          onOpenChange={(open) => {
            if (!open) {
              removeModal.requestClose();
            }
          }}
          onAfterClose={removeModal.afterClose}
          onReject={removeModal.requestClose}
          onResolve={handleRemove}
          resolveBtnProps={{ text: t("common.action.remove") }}
          tone="warning"
          data-testid="remove-event-participant-modal"
        />
      )}
    </>
  );

  function getParticipantActions(participant: EventParticipantDto): ContextMenuItem[] {
    return [
      {
        labels: { default: t("page.event.participants.action.edit-guests") },
        value: "edit-guests",
        icon: <Edit05Icon />,
        isHidden: event.maxGuestsPerParticipant === 0,
        callbackFn: () =>
          guestModal.open({
            mode: "edit",
            participant,
            maxGuests: computeMaxGuestsForExistingParticipant(event, participant.guestCount),
          }),
      },
      {
        labels: { default: t("common.action.remove") },
        value: "remove",
        icon: <Trash01Icon />,
        callbackFn: () => removeModal.open(participant),
      },
    ];
  }
}
